import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Mail } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import '../landing.css';
import { clearPreviewPlays, stashPreviewPlaysForSignup } from '../previewGate';

const base = import.meta.env.BASE_URL.replace(/\/$/, '');

// Auth CTAs — primary "Continue with Google" plus a subtler "Continue with
// Email" magic-link option (mirrors the old landing page). Self-contained: owns
// its own form state and calls the auth hook directly, so it can drop into both
// the hero and the closing section independently.
// The email route is hidden everywhere (googleOnly) until the Email link
// provider is switched on in the Firebase console: until then the send call
// fails with OPERATION_NOT_ALLOWED and the visitor just gets an alert. The
// form below still works, so it's one prop to put back.
// A visitor who came in on a /t/<id> share link has that path stashed before
// they're sent here to sign in. Both auth routes below would otherwise drop
// them at the app's front door, losing the technique they clicked on.
function pendingSharePath() {
  const p = sessionStorage.getItem('gnosis-post-auth');
  return p && p.startsWith('/t/') ? p : null;
}

/** The Google route, as one implementation. Anything on the page that offers
 *  to sign someone in must call this — a plain link to / looks like a CTA and
 *  does nothing, which is exactly what the header button used to do. */
export function useGoogleSignIn() {
  const { user, signInWithGoogle } = useAuth();
  return async () => {
    const search = window.location.search;
    const share = pendingSharePath();
    if (user) { window.location.href = base + (share ?? '/app') + search; return; }
    try {
      await signInWithGoogle();
      if (share) sessionStorage.removeItem('gnosis-post-auth');
      // They have an account now — the free-preview counter must never be able
      // to wall a member. Read one last time first, so the signup can be
      // attributed to however many free clips it took.
      stashPreviewPlaysForSignup();
      clearPreviewPlays();
      window.location.href = base + (share ?? '/app') + search;
    } catch (err) {
      console.error('SSO failed:', err);
      alert('Sign-in failed: ' + (err instanceof Error ? err.message : String(err)));
    }
  };
}

export function AuthCtas({ note, googleOnly = false }: { note?: string; googleOnly?: boolean }) {
  const { sendEmailLink } = useAuth();
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const handleGoogle = useGoogleSignIn();

  const handleEmail = async () => {
    if (!email.includes('@')) return;
    setSending(true);
    try {
      // Don't clobber a stashed share link — that one is the better destination.
      if (!pendingSharePath()) sessionStorage.setItem('gnosis-post-auth', '/checkout');
      await sendEmailLink(email);
      setSent(true);
    } catch (err) {
      console.error('Email link failed:', err);
      alert('Could not send sign-in link: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="lp-auth">
      <div className="lp-cta-row">
        <button className="lp-cta" onClick={handleGoogle}>
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continue with Google
        </button>

        {!googleOnly && !showEmail && !sent && (
          <button className="lp-cta lp-cta--gray" onClick={() => setShowEmail(true)}>
            <Mail size={18} />
            Continue with Email
          </button>
        )}
      </div>

      {showEmail && !sent && (
        <div className="lp-email-form">
          <input
            type="email"
            className="lp-email-input"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleEmail(); }}
            autoFocus
          />
          <button
            className="lp-email-send"
            onClick={handleEmail}
            disabled={!email.includes('@') || sending}
          >
            {sending ? 'Sending…' : 'Send Link'}
          </button>
        </div>
      )}

      {sent && <p className="lp-email-sent">Check your inbox for a sign-in link</p>}

      {note && <span className="lp-cta-note">{note}</span>}
    </div>
  );
}

// Phone mockup. Shows a captured screenshot (img) or looping clip (video) when
// provided; otherwise a faint label placeholder.
export const SHOT = (file: string) => `${import.meta.env.BASE_URL}landing/${file}`;
export function Phone({ tag, img, video }: { tag?: string; img?: string; video?: string }) {
  return (
    <div className="lp-phone" aria-hidden="true">
      <div className="lp-phone-screen">
        {video ? (
          <video className="lp-phone-media" src={video} autoPlay loop muted playsInline />
        ) : img ? (
          <img className="lp-phone-media" src={img} alt="" loading="lazy" />
        ) : (
          tag && <span className="lp-phone-tag">{tag}</span>
        )}
      </div>
    </div>
  );
}

// Scroll-reveal: fades + lifts children in once they enter the viewport.
export function Reveal({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} className={`lp-reveal${shown ? ' is-in' : ''} ${className}`}>{children}</div>;
}

interface FeatureProps {
  flip?: boolean;
  eyebrow: string;
  title: ReactNode;
  body: string;
  tag: string;
  img?: string;
  video?: string;
}

function Feature({ flip, eyebrow, title, body, tag, img, video }: FeatureProps) {
  return (
    <section className={`lp-feature${flip ? ' lp-feature--flip' : ''}`}>
      <Reveal className="lp-feature-text">
        <span className="lp-eyebrow">{eyebrow}</span>
        <h2 className="lp-feature-title">{title}</h2>
        <p className="lp-feature-body">{body}</p>
      </Reveal>
      <Reveal className="lp-feature-media">
        <Phone tag={tag} img={img} video={video} />
      </Reveal>
    </section>
  );
}

const FEATURES: FeatureProps[] = [
  {
    flip: true,
    eyebrow: 'Library',
    title: 'The whole game, organized.',
    body: 'Every technique, sorted from fundamentals to expert. Search, filter by position and level, and save what you’re drilling.',
    tag: 'Library',
    video: SHOT('library.mp4'),
  },
  {
    eyebrow: 'Favorites',
    title: 'Build your game.',
    body: 'Star techniques into your own practice queue. Your A-game, collected in one place and ready when you hit the mat.',
    tag: 'Favorites',
    video: SHOT('favorites.mp4'),
  },
  {
    flip: true,
    eyebrow: 'Community',
    title: 'Shape what’s next.',
    body: 'Weigh in on the topics steering the app. Gnosis is built with the people who actually train on it.',
    tag: 'Community',
    video: SHOT('community.mp4'),
  },
];

export default function LandingPage() {
  return (
    <div className="lp">
      {/* Hero */}
      <header className="lp-hero">
        <Reveal className="lp-hero-text">
          <div className="lp-lockup">
            {/* public/favicon.svg, brightened for the near-black hero */}
            <svg className="lp-mark" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="4" fill="#a06bff" />
              <circle cx="6" cy="7" r="2.5" fill="#a06bff" opacity=".72" />
              <circle cx="18" cy="7" r="2" fill="#a06bff" opacity=".66" />
              <circle cx="5" cy="16" r="1.5" fill="#a06bff" opacity=".6" />
              <circle cx="19" cy="15" r="3" fill="#a06bff" opacity=".78" />
              <circle cx="12" cy="4" r="1.5" fill="#a06bff" opacity=".6" />
              <circle cx="8" cy="19" r="2" fill="#a06bff" opacity=".72" />
            </svg>
            <span>GRAPPLING GNOSIS</span>
          </div>
          <h1 className="lp-hero-title">
            Just Watch and Learn
            <span className="lp-hero-accent">Get Solid Technique on Demand</span>
          </h1>
          <AuthCtas googleOnly />
        </Reveal>
        <Reveal className="lp-hero-media">
          <Phone tag="Gnosis" video={SHOT('discover.mp4')} />
        </Reveal>
      </header>

      {/* Feature sections */}
      {FEATURES.map((f) => <Feature key={f.eyebrow} {...f} />)}

      {/* Desktop showcase */}
      <section className="lp-desktop">
        <Reveal className="lp-desktop-text">
          <span className="lp-eyebrow">Cross Platform</span>
          <h2 className="lp-feature-title">Pretty cool on desktop.</h2>
        </Reveal>
        <Reveal className="lp-desktop-media">
          <div className="lp-browser" aria-hidden="true">
            <div className="lp-browser-bar">
              <span className="lp-browser-dot" />
              <span className="lp-browser-dot" />
              <span className="lp-browser-dot" />
            </div>
            <div className="lp-browser-screen">
              <video className="lp-browser-media" src={SHOT('desktop.mp4')} autoPlay loop muted playsInline />
            </div>
          </div>
        </Reveal>
      </section>

      <footer className="lp-footer">
        <AuthCtas note="30 days free · cancel anytime" googleOnly />
        <nav className="lp-footer-links">
          <a href={`${import.meta.env.BASE_URL}terms`}>Terms</a>
          <a href={`${import.meta.env.BASE_URL}privacy`}>Privacy</a>
          <a href={`${import.meta.env.BASE_URL}refund`}>Refund</a>
          <a href={`${import.meta.env.BASE_URL}copyright`}>Copyright</a>
        </nav>
      </footer>
    </div>
  );
}
